import React, { useMemo, useState } from 'react';
import data from 'emojibase-data/en/data.json';
import githubShortcodes from 'emojibase-data/en/shortcodes/github.json';

type EmojiData = {
  emoji?: string;
  annotation?: string;
  tags?: string[];
  hexcode: string;
  skins?: EmojiData[];
};

type ShortcodeMap = Record<string, string[] | string>;

/** Flatten base emojis + their skin variants into a single list */
function flattenEmojis(list: EmojiData[]): EmojiData[] {
  const out: EmojiData[] = [];
  for (const e of list) {
    out.push(e);
    if (e.skins) out.push(...e.skins);
  }
  return out;
}

/** Normalize shortcodes record (values can be string | string[]) */
function getShortcodesFor(hex: string, map: ShortcodeMap): string[] {
  const val = map[hex];
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

type MergedItem = {
  emoji: string;
  annotation: string;
  tags: string[];
  hexcode: string;
  shortcodes: string[];
};

function buildMergedIndex(
  base: EmojiData[],
  scMap: ShortcodeMap
): MergedItem[] {
  const flattened = flattenEmojis(base);
  return flattened
    .filter(e => e.emoji)
    .map(e => ({
      emoji: e.emoji || '',
      annotation: e.annotation || '',
      tags: e.tags || [],
      hexcode: e.hexcode,
      shortcodes: getShortcodesFor(e.hexcode, scMap),
    }))
    // only keep entries that actually have at least one shortcode
    .filter(e => e.shortcodes.length > 0);
}

type EmojiPickerProps = {
  onPick: (shortcode: string, emojiChar: string) => void;
  maxItems?: number;
};

export function EmojiPicker({ onPick, maxItems = 1000 }: EmojiPickerProps) {
  const [query, setQuery] = useState('');

  const items = useMemo(() => {
    const merged = buildMergedIndex(
      data as unknown as EmojiData[],
      githubShortcodes as ShortcodeMap
    );

    const q = query.trim().toLowerCase();
    if (!q) {
      return merged.slice(0, maxItems);
    }

    const filtered = merged.filter(e =>
      e.shortcodes.some(sc => sc.includes(q)) ||
      e.annotation.toLowerCase().includes(q) ||
      e.tags.some(t => t.toLowerCase().includes(q))
    );

    return filtered.slice(0, Math.max(maxItems, 200));
  }, [query, maxItems]);

  return (
    <div
      role="dialog"
      aria-label="Emoji picker"
      className="rounded-2xl border p-3 shadow-lg min-w-60 bg-white"
      style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}
    >
      <input
        aria-label="Search emojis"
        placeholder="Search emoji or :shortcode:"
        value={query}
        onChange={e => setQuery(e.target.value)}
        className="w-full border rounded-lg px-2 py-1 mb-2 text-sm"
      />

      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 2,
          maxHeight: 260,
          overflow: 'auto',
        }}
      >
        {items.map(item => {
          const sc = item.shortcodes[0];
          return (
            <button
              key={`${item.hexcode}-${sc}`}
              type="button"
              className="text-xl leading-none p-1 rounded-lg hover:bg-gray-100 focus:bg-gray-100 focus:outline-none w-10 h-10"
              title={`:${sc}:`}
              aria-label={`:${sc}:`}
              onClick={() => onPick(sc, item.emoji)}
            >
              {item.emoji}
            </button>
          );
        })}
      </div>

      {/* <div className="mt-2 text-xs text-gray-500">
        Tip: inserts <code>:shortcode:</code> so it renders via <code>remark-emoji</code>.
      </div> */}
    </div>
  );
}
